import { useTranslation } from "react-i18next";
import {
  EWalletConfiguration,
  PaymentGateway,
} from "store/config/ewallet-configuration";
import { useAddNotification } from "./useAddNotification";
import { PaymentMethod } from "store/paymentMethods/paymentMethods-state";
import { useAcceptJs } from "react-acceptjs";
import jsonp from "jsonp";

interface SlimCDResponse {
  reply: {
    response: string;
    responsecode: number;
    description: string;
    datablock: {
      authcode: string;
      approved: string;
      gateid: string;
      cardtype: string;
    };
  };
}

interface CardTokenResponse {
  response: CardTokenResponses;
  data?: PaymentMethod;
}

export enum CardTokenResponses {
  SUCCESS = 1,
  CARDNUMBER_ERROR = 2,
  EXPIRATION_ERROR = 3,
  UNKNOWN_ERROR = 4,
}

export const useCardTokenization = (effectiveConfig: EWalletConfiguration) => {
  const addNotification = useAddNotification();
  const { t } = useTranslation(["common"]);
  const acceptJsEnv = import.meta.env.REACT_APP_ACCEPTJS_ENV;

  const { dispatchData } = useAcceptJs({
    environment: acceptJsEnv,
    authData: {
      clientKey: effectiveConfig?.authorizeNetConfiguration?.clientKey ?? "",
      apiLoginID: effectiveConfig?.authorizeNetConfiguration?.apiLoginId ?? "",
    },
  });

  const tokenizeCard = async (
    cardFormData: PaymentMethod,
  ): Promise<CardTokenResponse | void> => {
    const cvv2 = cardFormData.cvv2 ?? "";
    const safeCardFormData = { ...cardFormData };

    if (effectiveConfig.paymentGateway === PaymentGateway.SlimCD) {
      try {
        const dataConfig = {
          username:
            effectiveConfig?.slimCDConfiguration?.publicApiCredential ?? "",
          transtype: "LOAD",
          amount: "0.00",
          cardnumber: cardFormData.tokenizedCardData
            ? cardFormData.tokenizedCardData?.replaceAll(" ", "")
            : "",
          expmonth: cardFormData.expirationDate
            ? cardFormData.expirationDate?.slice(0, 2)
            : "",
          expyear: cardFormData.expirationDate
            ? cardFormData.expirationDate?.slice(2)
            : "",
          cvv2,
        };

        const slimCdUri = new URL(
          "https://trans.slimcd.com/soft/json/jsonpayment.asp",
        );
        slimCdUri.searchParams.append("username", dataConfig.username);
        slimCdUri.searchParams.append("transtype", "LOAD");
        slimCdUri.searchParams.append("amount", "0.00");
        slimCdUri.searchParams.append("cardnumber", dataConfig.cardnumber);
        slimCdUri.searchParams.append("expmonth", dataConfig.expmonth);
        slimCdUri.searchParams.append("expyear", dataConfig.expyear);
        if (dataConfig.cvv2) {
          slimCdUri.searchParams.append("cvv2", dataConfig.cvv2);
        }

        const slimCdResponse = await new Promise<SlimCDResponse>(
          (resolve, reject) => {
            jsonp(slimCdUri.toString(), {}, (err, data) => {
              if (err) {
                reject(err);
              } else {
                resolve(data as SlimCDResponse);
              }
            });
          },
        );

        const { responsecode, datablock } = slimCdResponse.reply;
        if (responsecode === 0 && datablock.approved === "Y") {
          return {
            response: CardTokenResponses.SUCCESS,
            data: {
              ...safeCardFormData,
              tokenizedCardData: datablock.gateid,
            },
          };
        } else {
          addNotification({
            translatedMessages: [t("common:invalid-card-information")],
            type: "ERROR",
            closable: true,
          });
          return { response: CardTokenResponses.CARDNUMBER_ERROR };
        }
      } catch (err: any) {
        if (err as Error) {
          addNotification({
            messages: [err.message],
            type: "ERROR",
            closable: true,
          });
        }
        return { response: CardTokenResponses.UNKNOWN_ERROR };
      }
    } else if (effectiveConfig.paymentGateway === PaymentGateway.AuthorizeNet) {
      try {
        const cardNumber = cardFormData.tokenizedCardData
          ? cardFormData.tokenizedCardData.replace(/\s/g, "")
          : "";
        const month = cardFormData.expirationDate
          ? cardFormData.expirationDate.slice(0, 2)
          : "";
        const year = cardFormData.expirationDate
          ? cardFormData.expirationDate.slice(2)
          : "";

        const response = await dispatchData({
          cardData: {
            cardNumber: cardNumber,
            month: month,
            year: year,
            cardCode: cvv2 ?? "",
          },
        });

        if (response.messages.resultCode === "Ok") {
          return {
            response: CardTokenResponses.SUCCESS,
            data: {
              ...safeCardFormData,
              tokenizedCardData: response.opaqueData.dataValue,
            },
          };
        } else {
          const errorMessage =
            response.messages.message?.[0]?.text ||
            t("common:invalid-card-information");
          addNotification({
            translatedMessages: [errorMessage],
            type: "ERROR",
            closable: true,
          });
          return { response: CardTokenResponses.CARDNUMBER_ERROR };
        }
      } catch (err: any) {
        addNotification({
          messages: [err?.message || t("common:invalid-card-information")],
          type: "ERROR",
          closable: true,
        });
        return { response: CardTokenResponses.UNKNOWN_ERROR };
      }
    }
    return { response: CardTokenResponses.UNKNOWN_ERROR };
  };

  return {
    tokenizeCard,
  };
};

export default useCardTokenization;
